import { Box, Grid, Typography } from "@mui/material";
import CancelButton from "components/buttons/CancelButton";
import DeleteButton from "components/buttons/DeleteButton";
import SaveButton from "components/buttons/SaveButton";
import CustomTextField from "components/inputs/CustomTextField";
import DropdownField from "components/inputs/DropdownField";
import userResponseFields from "config/forms/userResponseFields";
import PropTypes from "prop-types";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useIsMutating } from "@tanstack/react-query";
import { jwtDecode } from "jwt-decode";
import { observer } from "mobx-react-lite";
import { useForm } from "react-hook-form";
import DateField from "components/inputs/DateField";
import { useGetAllRoles } from "hooks/queries/useAuthSetup";
import { useEffect, useState } from "react";
import DeleteModal from "components/modals/DeleteModal";
import ExitFormModal from "components/modals/ExitFormModal";
import { useAuthStore } from "stores/AuthStore";
import { useAlert } from "hooks/useAlert";

const UserViewEditForm = ({
  selectedUser,
  isEditing = false,
  onSave,
  onCancel,
  onDelete,
}) => {
  const authStore = useAuthStore();
  const { showError } = useAlert();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isUpdateLoading = useIsMutating({ mutationKey: ["updateUser"] }) > 0;
  const isDeleteLoading = useIsMutating({ mutationKey: ["deleteUser"] }) > 0;
  const { data: availableRoles } = useGetAllRoles({
    select: (data) => data?.result,
  });

  const normalizeSelectValue = (value) => {
    if (typeof value === "object" && value !== null) {
      return value.id ?? value.label ?? "";
    }

    return value;
  };

  const decodeCurrentUser = () => {
    if (!authStore.token) {
      return {};
    }

    try {
      return jwtDecode(authStore.token);
    } catch {
      return {};
    }
  };

  const normalizeValue = (value) => {
    if (value === null || value === undefined) {
      return null;
    }

    return String(value).trim().toLowerCase();
  };

  const currentUser = decodeCurrentUser();
  const currentUserId =
    currentUser[
      "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
    ] || currentUser.nameid || currentUser.sub;
  const currentUserEmail =
    currentUser[
      "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
    ] || currentUser.email;
  const currentUserName =
    currentUser[
      "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"
    ] || currentUser.unique_name || currentUser.preferred_username;

  const isEditingCurrentSuperAdmin =
    authStore.isSuperAdmin &&
    (normalizeValue(selectedUser?.id) === normalizeValue(currentUserId) ||
      normalizeValue(selectedUser?.email) === normalizeValue(currentUserEmail) ||
      normalizeValue(selectedUser?.userName) === normalizeValue(currentUserName));

  const schema = yup.object(
    userResponseFields.reduce((acc, field) => {
      let fieldSchema;

      if (
        field.type === "text" ||
        field.type === "textarea" ||
        field.type === "file"
      ) {
        fieldSchema = yup.string();
      } else if (field.type === "select" || field.type === "boolean") {
        fieldSchema = yup
          .string()
          .transform((value, originalValue) =>
            normalizeSelectValue(originalValue) ?? value,
          );
      } else if (field.type === "number") {
        fieldSchema = yup
          .number()
          .transform((value) => (isNaN(value) ? undefined : value));
      } else if (field.type === "date") {
        fieldSchema = yup.date().transform((value, originalValue) => {
          return isNaN(Date.parse(originalValue)) ? undefined : value;
        });
      }

      if (field.required) {
        fieldSchema = fieldSchema.required("Το πεδίο είναι υποχρεωτικό");
      } else {
        fieldSchema = fieldSchema.nullable();
      }

      if (field.validation === "custom" && field.regex) {
        fieldSchema = fieldSchema.matches(field.regex, field.validationMessage);
      }

      acc[field.name] = fieldSchema.typeError(
        field.validationMessage || "Μη έγκυρη τιμή",
      );

      return acc;
    }, {}),
  );

  const getDataValueFromField = (fieldName) => {
    const fieldMap = userResponseFields.reduce((acc, field) => {
      acc[field.name] = field.name;
      return acc;
    }, {});

    const path = fieldMap[fieldName];
    if (typeof path === "function") {
      return path();
    } else if (typeof path === "string") {
      const value = path
        .split(".")
        .reduce((acc, key) => (acc ? acc[key] : undefined), selectedUser);
      return value !== undefined && value !== null ? value : null;
    } else {
      return "";
    }
  };

  const handleFormSubmit = (data) => {
    const normalizedData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, normalizeSelectValue(value)]),
    );

    if (isEditingCurrentSuperAdmin && normalizedData.status === "Inactive") {
      showError("Δεν μπορείτε να απενεργοποιήσετε τον λογαριασμό σας");
      return;
    }

    onSave(normalizedData);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const formFields = userResponseFields.map((field) => {
    if (field.name === "status") {
      return {
        ...field,
        editable: authStore.isSuperAdmin,
        options: field.options?.map((option) => ({
          ...option,
          isDisabled:
            isEditingCurrentSuperAdmin && option.id === "Inactive",
        })),
      };
    }

    return field;
  });

  const defaultValues = {
    ...Object.fromEntries(
      formFields.map((field) => [
        field.name,
        getDataValueFromField(field.name),
      ]),
    ),
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [selectedUser, reset]);

  const getInputElementByFieldType = (field) => {
    const defaultValue = getDataValueFromField(field.name);

    if (
      field.type === "text" ||
      field.type === "number" ||
      field.type === "textarea" ||
      field.type === "file"
    ) {
      return (
        <CustomTextField
          {...field}
          value={defaultValue}
          disabled={!isEditing}
          control={control}
        />
      );
    } else if (field.type === "select" || field.type === "boolean") {
      let availableOptions =
        field.type === "boolean" ? ["Ναι", "Όχι"] : field.options;

      if (field.name === "role") {
        availableOptions = availableRoles?.map((option) => option.name);
      }

      return (
        <DropdownField
          {...field}
          control={control}
          value={defaultValue}
          disabled={!isEditing}
          options={availableOptions}
          boolean={field.type === "boolean"}
        />
      );
    } else if (field.type === "date") {
      return (
        <DateField
          {...field}
          value={defaultValue}
          disabled={!isEditing}
          control={control}
        />
      );
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Box display="flex" flexDirection="column" gap={4}>
          <Grid container spacing={2}>
            {formFields
              .filter((field) => field.display !== false)
              .map((field) => (
                <Grid item xs={12} md={4} key={field.name}>
                  {getInputElementByFieldType(field)}
                </Grid>
              ))}
          </Grid>

          {Object.keys(errors).length > 0 && (
            <Typography color="error" fontSize={14}>
              Παρακαλώ ελέγξτε ότι όλα τα πεδία έχουν συμπληρωθεί σωστά
            </Typography>
          )}

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <Box display="flex" gap={2} justifyContent={"flex-start"}>
              <CancelButton
                disabled={!isEditing}
                onClick={() => {
                  reset();
                  onCancel();
                }}
              />
              <DeleteButton
                onClick={handleOpenModal}
                text="Διαγραφή Χρήστη"
                disabled={!isEditing}
                isLoading={isDeleteLoading}
              />
            </Box>

            <SaveButton
              disabled={!isEditing || !isDirty}
              isLoading={isUpdateLoading}
            />

            <DeleteModal
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              onDelete={onDelete}
              itemTitle={`${selectedUser?.name} ${selectedUser?.surname}`}
              title="Πρόκειται να διαγράψετε τον χρήστη"
              description="Είστε βέβαιοι ότι θέλετε να προχωρήσετε στη διαγραφή του χρήστη;"
            />
          </Box>
        </Box>
      </form>

      <ExitFormModal isFormDirty={isDirty} />
    </>
  );
};

UserViewEditForm.propTypes = {
  selectedUser: PropTypes.object.isRequired,
  isEditing: PropTypes.bool,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  onDelete: PropTypes.func,
};

export default observer(UserViewEditForm);
